import React from "react";
import { Box, Button, Typography, Stack } from "@mui/material";
import { motion } from "framer-motion";

import mainHero from "../../static/media/home/main-hero.webp";
import cuadroHero from "../../static/media/home/Entorno.webp";

const MotionButton = motion(Button);
const MotionBox = motion(Box);

const HeroSection = () => {
  const scrollToModules = () => {
    const section = document.getElementById("modulos");

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const scrollToPricing = () => {
    const section = document.getElementById("precios");

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        backgroundImage: `url(${mainHero})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        pt: {
          xs: "95px",
          md: "110px",
        },
        minHeight: "100vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "1fr 1fr",
          },
          flex: 1,
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* LEFT (TEXTOS Y BOTONES) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: { xs: 3, md: 6, lg: 8 },
            py: { xs: 6, md: 8, lg: 0 },
            height: "100%",
            // CAMBIO: Aseguramos que el texto esté siempre por encima de la imagen en mobile
            position: "relative",
            zIndex: 2,
          }}
        >
          <MotionBox
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            sx={{
              maxWidth: 650,
              position: "relative",
            }}
          >
            {/* GLOW */}
            <MotionBox
              animate={{
                scale: [1, 1.08, 1],
                opacity: [0.15, 0.25, 0.15],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
              }}
              sx={{
                position: "absolute",
                width: 300,
                height: 300,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(44,95,190,0.18) 0%, transparent 70%)",
                top: -100,
                left: -100,
                zIndex: -1,
              }}
            />

            {/* TITLE */}
            <Typography
              component="h1"
              sx={{
                fontWeight: 700,
                lineHeight: 1.1,
                color: "#1A2B4A",
                fontSize: {
                  xs: "2.5rem",
                  sm: "3rem",
                  md: "3.5rem",
                  lg: "3.2rem",
                  xl: "4rem",
                },
              }}
            >
              Simplifica tu{" "}
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(135deg, #2C5FBE 0%, #4F8CFF 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                facturación electrónica
              </Box>{" "}
              y gestiona tu negocio fácilmente.
            </Typography>

            {/* SUBTITLE */}
            <Typography
              sx={{
                mt: 2.5,
                color: "#000000",
                fontSize: { xs: "0.95rem", md: "1.1rem" },
                lineHeight: 1.6,
                maxWidth: "560px",
              }}
            >
              Emite comprobantes en segundos, automatiza tu inventario y
              optimiza tus ventas con una plataforma intuitiva.
            </Typography>

            {/* BUTTONS */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={4}>
              <MotionButton
                component="a"
                href="https://api.whatsapp.com/send/?phone=593985092679&text=Hola,%20quiero%20solicitar%20una%20demo%20gratuita%20de%20FactuSysDR"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
                variant="contained"
                sx={{
                  bgcolor: "#2C5FBE",
                  borderRadius: "14px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 750,
                  fontSize: "1rem",
                  textTransform: "none",
                  boxShadow: "0 15px 35px rgba(44,95,190,0.25)",
                  "&:hover": { bgcolor: "#214A96" },
                }}
              >
                Solicitar demostración
              </MotionButton>

              <MotionButton
                whileHover={{ y: -3 }}
                variant="outlined"
                onClick={scrollToPricing}
                sx={{
                  borderRadius: "14px",
                  px: 4,
                  py: 1.5,
                  borderWidth: "2px",
                  borderColor: "#2C5FBE",
                  color: "#2C5FBE",
                  bgcolor: "#ffffff", // Fondo completamente blanco
                  fontWeight: 750,
                  textTransform: "none",
                  "&:hover": {
                    borderWidth: "2px",
                    borderColor: "#2C5FBE",
                    bgcolor: "#f8fafc", // Un blanco ligeramente grisáceo/azulado al pasar el mouse
                  },
                }}
              >
                Ver planes
              </MotionButton>
            </Stack>
          </MotionBox>
        </Box>

        {/* RIGHT (IMAGEN Y DECORACIÓN) */}
        <Box
          sx={{
            // CAMBIO: En mobile es 'absolute' para quedarse de fondo, en desktop vuelve a 'relative' a su grilla
            position: { xs: "absolute", lg: "relative" },
            inset: 0,
            overflow: "hidden",
            height: "100%", // Cubre toda la altura en ambas vistas
            zIndex: { xs: 0, lg: 1 }, // En mobile pasa al fondo
          }}
        >
          {/* IMAGEN */}
          <Box
            component="img"
            src={cuadroHero}
            alt="FactuSysDR"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              clipPath: "polygon(5% 0%, 100% 0%, 100% 100%, 28% 95%)",
              zIndex: 1,
            }}
          />

          {/* TRIÁNGULOS ORIGINALES - NO TOCAR */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "#2C5FBE",
              zIndex: 2,
              clipPath: "polygon(-5% -5%, 45% -6%, 16% 45%, 0 30%)",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "#fff",
              clipPath: "polygon(0 0, 25% 0, 0 15%)",
              zIndex: 3,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "#2C5FBE",
              clipPath: "polygon(0 85%, 18% 100%, 0 65%)",
              zIndex: 2,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "#2C5FBE",
              clipPath: "polygon(0 100%, 35% 100%, 30% 75%)",
              zIndex: 2,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              zIndex: 4,
              "&::after": {
                content: '""',
                position: "absolute",
                width: "160%",
                height: "4px",
                background: "#fff",
                bottom: 0,
                left: 0,
                transform: "rotate(-33deg)",
                transformOrigin: "left bottom",
              },
            }}
          />

          {/* NUEVO: OVERLAY PARA MOBILE (Glassmorphism) */}
          {/* Este filtro solo se muestra en pantallas pequeñas (xs a md) y se oculta en desktop (lg) */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              // Degradado blanco sutil, más fuerte donde va el texto, más suave hacia la esquina
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.65) 50%)",
              backdropFilter: "blur(5px)", // Efecto cristal atractivo
              WebkitBackdropFilter: "blur(5px)", // Soporte para Safari
              zIndex: 5, // Por encima de los triángulos y la imagen
              display: { xs: "block", lg: "none" }, // Oculto en Desktop
              pointerEvents: "none", // Permite que los clics pasen al botón de abajo
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
